import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const emptyForm = { title: "", subtitle: "", image: "", cta_text: "", cta_link: "", active: true, position: "home-hero" };

export default function AdminBanners() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => setItems((await api.get("/admin/banners")).data);
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/banners/${editingId}`, form);
      else await api.post("/admin/banners", form);
      toast.success("Saved"); setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/banners/${id}`); load(); };

  return (
    <div data-testid="admin-banners">
      <Header title="Banners" onAdd={() => { setForm(emptyForm); setEditingId(null); setShow(true); }} addLabel="New Banner" />
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {items.map((b) => (
          <div key={b.id} className="card-luxury overflow-hidden">
            {b.image && <img src={b.image} alt={b.title} className="w-full aspect-[16/8] object-cover" />}
            <div className="p-5">
              <div className="text-xs uppercase tracking-widest text-sky-400">{b.position}</div>
              <div className="text-white font-medium mt-1">{b.title}</div>
              <div className="text-slate-400 text-sm">{b.subtitle}</div>
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-xs ${b.active ? "text-emerald-400" : "text-slate-500"}`}>{b.active ? "Active" : "Inactive"}</span>
                <div className="flex">
                  <IconBtn onClick={() => { setForm({ ...b }); setEditingId(b.id); setShow(true); }}><Edit className="w-3.5 h-3.5" /></IconBtn>
                  <IconBtn onClick={() => remove(b.id)} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 text-sm text-center py-10 col-span-2">No banners yet.</div>}
      </div>

      {show && (
        <Modal title={editingId ? "Edit banner" : "New banner"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></Field>
            <Field label="Subtitle"><Textarea rows={2} value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
            <Field label="Image URL"><Input value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="CTA text"><Input value={form.cta_text || ""} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} /></Field>
              <Field label="CTA link"><Input value={form.cta_link || ""} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} /></Field>
              <Field label="Position"><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></Field>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-slate-300">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
